import { Outlet, Link } from '@tanstack/react-router'
import {
  LayoutDashboard,
  Navigation,
  Map,
  Car,
  ShieldCheck,
  Wallet,
  Users,
  Settings,
  Search,
  Bell,
  LogOut,
  type LucideIcon,
} from 'lucide-react'
import { useAuthStore } from '@/lib/auth/auth-store'
import { AmineLockup } from '@/components/brand/amine-logo'
import { Avatar } from '@/components/ui/avatar'
import { PageHeaderProvider } from '@/components/page-header'
import { usePageHeaderValue } from '@/components/page-header-context'

interface NavItem {
  to:
    | '/dashboard'
    | '/rides'
    | '/live-map'
    | '/drivers'
    | '/kyc'
    | '/billing'
    | '/users'
    | '/settings'
  label: string
  icon: LucideIcon
}

const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
  { to: '/rides', label: 'Courses', icon: Navigation },
  { to: '/live-map', label: 'Carte temps réel', icon: Map },
  { to: '/drivers', label: 'Chauffeurs', icon: Car },
  { to: '/kyc', label: 'Vérification KYC', icon: ShieldCheck },
  { to: '/billing', label: 'Recouvrement', icon: Wallet },
  { to: '/users', label: 'Utilisateurs', icon: Users },
  { to: '/settings', label: 'Paramètres', icon: Settings },
]

function Topbar() {
  const { title, subtitle } = usePageHeaderValue()
  return (
    <header className="h-[72px] shrink-0 border-b border-line flex items-center px-7 gap-5 bg-bg">
      <div className="flex-1 min-w-0">
        <h1 className="font-display font-bold text-xl text-ink tracking-tight">{title}</h1>
        {subtitle && <p className="text-[12.5px] text-muted mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 h-[38px] px-3.5 rounded-pill bg-surface border border-line w-[220px]">
        <Search size={16} className="text-muted" />
        <span className="text-[13px] text-muted">Rechercher un chauffeur…</span>
      </div>
      <div className="relative w-[38px] h-[38px] rounded-full bg-surface border border-line flex items-center justify-center">
        <Bell size={18} className="text-ink-soft" />
        <span className="absolute top-2 right-[9px] w-[7px] h-[7px] rounded-full bg-danger" />
      </div>
    </header>
  )
}

export default function Layout() {
  const { logout, user } = useAuthStore()
  return (
    <PageHeaderProvider>
      <div className="flex h-screen bg-bg text-ink">
        {/* sidebar */}
        <aside className="w-[248px] shrink-0 bg-surface border-r border-line flex flex-col px-3.5 py-[22px]">
          <div className="px-1 pb-1">
            <AmineLockup height={48} />
          </div>
          <nav className="mt-6 flex flex-col gap-[3px]">
            <div className="text-[10.5px] font-semibold tracking-wide uppercase text-muted px-2.5 pb-2">
              Opérations
            </div>
            {navItems.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className="flex items-center gap-3 h-[42px] px-3 rounded-[11px] group"
                activeProps={{ className: 'bg-ink' }}
                inactiveProps={{ className: 'hover:bg-surfaceAlt' }}
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      size={18}
                      strokeWidth={2.1}
                      className={isActive ? 'text-primary' : 'text-muted'}
                    />
                    <span
                      className={`flex-1 text-[13.5px] ${
                        isActive ? 'font-semibold text-surface' : 'font-medium text-ink-soft'
                      }`}
                    >
                      {label}
                    </span>
                    {to === '/kyc' && (
                      <span className="min-w-[20px] h-5 px-1.5 rounded-[10px] bg-primary text-primary-ink font-mono text-[11px] font-bold flex items-center justify-center">
                        7
                      </span>
                    )}
                  </>
                )}
              </Link>
            ))}
          </nav>
          <div className="flex-1" />
          {/* admin user */}
          <div className="flex items-center gap-2.5 p-2.5 rounded-card bg-surfaceAlt">
            <Avatar name={user?.email ?? 'Admin'} size={36} />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-ink truncate">
                {user?.email ?? 'Admin'}
              </div>
              <div className="font-mono text-[10.5px] text-muted truncate">ROLE · ADMIN</div>
            </div>
            <button
              onClick={logout}
              aria-label="Déconnexion"
              title="Déconnexion"
              className="shrink-0 p-1.5 rounded-lg text-muted hover:text-danger hover:bg-surface transition-colors"
            >
              <LogOut size={16} />
            </button>
          </div>
        </aside>

        {/* main */}
        <div className="flex-1 flex flex-col min-w-0">
          <Topbar />
          <main className="flex-1 overflow-auto p-7">
            <Outlet />
          </main>
        </div>
      </div>
    </PageHeaderProvider>
  )
}
